<template>
  <div>
    <div class="list-wrapper">
      <button
        v-for="(attachment, index) in attachments"
        :key="index"
        class="item"
        :class="{
          loading: loadingPath === attachment.attachment_path,
          unavailable: unavailablePaths[attachment.attachment_path],
        }"
        :disabled="!!loadingPath || unavailablePaths[attachment.attachment_path]"
        @click="handleAttachmentClick(attachment)"
      >
        <!-- attachment_type can only be video so the icon is always the same for now -->
        <div
          class="tw:bg-main-blue tw:w-8 tw:h-8 tw:flex tw:items-center tw:justify-center tw:rounded-lg"
        >
          <div class="attachment-icon-wrapper">
            <svg viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M9 4.46559C9.66667 4.85049 9.66667 5.81274 9 6.19764L1.5 10.5278C0.833333 10.9127 -3.3649e-08 10.4315 0 9.66175L3.78552e-07 1.00149C4.12201e-07 0.231691 0.833334 -0.249434 1.5 0.135467L9 4.46559Z"
                fill="#F8FAFC"
              />
            </svg>
          </div>
        </div>
        <div v-if="loadingPath === attachment.attachment_path">Loading recording...</div>
        <div v-else-if="unavailablePaths[attachment.attachment_path]">Recording unavailable</div>
        <div v-else>Screen Recording</div>
      </button>
    </div>

    <b-modal
      id="video-modal"
      v-model="showModal"
      class="video-modal-root"
      size="lg"
      hide-footer
      hide-header
      centered
      @hidden="resetModalState"
    >
      <div class="video-modal">
        <img
          :src="closeModalIcon"
          alt="close modal"
          class="modal-close"
          @click="showModal = false"
        />

        <div v-if="attachmentNotice" class="video-unavailable">
          <div class="video-unavailable__title">
            {{ attachmentUnavailableText.title }}
          </div>
          <div class="video-unavailable__subtitle">
            {{ attachmentNotice }}
          </div>
        </div>

        <video
          v-else-if="videoUrl"
          :src="videoUrl"
          controls
          autoplay
          class="video-player"
          @error="handleVideoError"
        />

        <a v-if="videoUrl" :href="videoUrl" target="_blank"> Download </a>
      </div>
    </b-modal>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import closeModalIcon from '../../assets/images/closeModal.svg';
import { errorNotification } from '@/services/notificationService/notificationService';

const ATTACHMENT_UNAVAILABLE_TEXT = {
  title: 'The video is no longer available.',
  subtitle: 'The file was removed from storage. Report artifacts are kept only for a limited time.',
};

// Returns text like "Recordings are stored for 30 days. Were deleted on 11 Apr 2026."
function buildRetentionNotice(retentionDays, createdAt) {
  const created = dayjs(createdAt);
  if (!created.isValid()) return ATTACHMENT_UNAVAILABLE_TEXT.subtitle;

  const deleteDate = created.add(retentionDays, 'day');
  const formatted = deleteDate.format('DD MMM YYYY');
  const isPast = dayjs().isAfter(deleteDate);

  if (isPast) {
    return `Recordings are stored for ${retentionDays} days. They were deleted on ${formatted}.`;
  }

  return `Recordings are stored for ${retentionDays} days. Will be deleted on ${formatted}.`;
}

export default {
  name: 'VulnAttachmentList',
  props: {
    attachments: {
      type: Array,
      required: true,
    },
    jobId: {
      type: String,
      required: true,
    },
    // Number of days artifacts are stored; null for legacy jobs with no retention info
    retentionDays: {
      type: Number,
      default: null,
    },
    // ISO date string of job creation, used together with retentionDays to compute expiry
    createdAt: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      videoUrl: null,
      showModal: false,
      closeModalIcon,
      attachmentUnavailableText: ATTACHMENT_UNAVAILABLE_TEXT,
      urlCache: new Map(),
      noticeCache: new Set(),
      unavailablePaths: {},
      loadingPath: null,
      attachmentNotice: '',
    };
  },
  computed: {
    isRetentionExpired() {
      if (!this.retentionDays || !this.createdAt) return false;
      const deleteDate = dayjs(this.createdAt).add(this.retentionDays, 'day');
      return dayjs().isAfter(deleteDate);
    },
    retentionExpiredNotice() {
      if (!this.retentionDays || !this.createdAt) return ATTACHMENT_UNAVAILABLE_TEXT.subtitle;
      return buildRetentionNotice(this.retentionDays, this.createdAt);
    },
  },
  methods: {
    /**
     * @typedef {{ attachment_path: string, attachment_type: string }} TAttachment
     * @param {TAttachment} attachment
     */
    async handleAttachmentClick(attachment) {
      if (!this.jobId) return;

      const cacheKey = attachment.attachment_path;

      if (this.urlCache.has(cacheKey)) {
        this.videoUrl = this.urlCache.get(cacheKey);
        this.showModal = true;
        return;
      }

      if (this.noticeCache.has(cacheKey)) {
        this.showUnavailableAttachment(cacheKey, this.attachmentUnavailableText.subtitle);
        return;
      }

      // If retention is known and already expired — no need to hit the API
      if (this.isRetentionExpired) {
        this.markAttachmentUnavailable(cacheKey);
        this.showUnavailableAttachment(cacheKey, this.retentionExpiredNotice);
        return;
      }

      this.loadingPath = attachment.attachment_path;

      try {
        const { data } = await this.axios.get(
          `/private/ui/s3/${this.jobId}/vulnerability-attachment-url`,
          {
            params: {
              attachmentPath: attachment.attachment_path,
            },
          }
        );

        this.loadingPath = null;

        if (!data || !data.url) {
          this.markAttachmentUnavailable(cacheKey);
          this.showUnavailableAttachment(cacheKey, this.attachmentUnavailableText.subtitle);
          return;
        }

        this.urlCache.set(cacheKey, data.url);
        this.videoUrl = data.url;
        this.showModal = true;
      } catch (error) {
        if (this.isAttachmentUnavailableError(error)) {
          this.markAttachmentUnavailable(cacheKey);
          this.showUnavailableAttachment(cacheKey, this.getAttachmentUnavailableMessage(error));
          return;
        }

        errorNotification({ message: 'Failed to load the attachment.' });
      } finally {
        this.loadingPath = null;
      }
    },
    markAttachmentUnavailable(cacheKey) {
      this.noticeCache.add(cacheKey);
      this.$set(this.unavailablePaths, cacheKey, true);
    },
    showUnavailableAttachment(cacheKey, message) {
      if (cacheKey) {
        this.$set(this.unavailablePaths, cacheKey, true);
      }
      this.videoUrl = null;
      this.attachmentNotice = message || this.attachmentUnavailableText.subtitle;
      this.showModal = true;
    },
    resetModalState() {
      this.videoUrl = null;
      this.attachmentNotice = '';
    },
    handleVideoError() {
      this.showModal = false;
      const notify = this.errorNotification;
      setTimeout(() => {
        notify({
          message:
            'Recording is no longer available. It may have been removed due to storage retention policy.',
        });
      }, 300);
    },
    isAttachmentUnavailableError(error) {
      const status = error?.response?.status;
      return status === 403 || status === 404 || status === 410;
    },
    getAttachmentUnavailableMessage(error) {
      return error?.response?.data?.message || this.attachmentUnavailableText.subtitle;
    },
    errorNotification,
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/css/fonts';
@import '../../assets/css/variables';

.list-wrapper {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  @media screen and (max-width: 767px) {
    flex-wrap: nowrap;
    flex-direction: column;
  }
}

.item {
  max-width: 286px;
  flex: 0 0 286px;
  max-height: 56px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 16px;
  user-select: none;
  background: #f8fafc;
  transition: background-color 0.3s ease;
  @include Aspekta-font(500, 14px, normal, #021631);

  @media screen and (max-width: 767px) {
    flex: 1 0 auto;
    max-width: 100%;
    width: 100%;
  }

  @media screen and (min-width: 1024px) {
    cursor: pointer;

    &:hover {
      background: #e2edf9;
    }
  }

  &.loading {
    opacity: 0.7;
    pointer-events: none;
  }

  &.unavailable {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.attachment-icon-wrapper {
  border-radius: 8px;
  width: 10px;
  height: 11px;
  margin-left: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-modal-root {
  position: relative;
}

.modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  cursor: pointer;
  z-index: 1;

  &:hover {
    opacity: 0.65;
  }
}

.video-modal {
  padding: 20px 0;

  .video-unavailable {
    padding: 40px 24px;
    text-align: center;
    @include Aspekta-font(500, 16px, normal, #021631);
  }

  .video-unavailable__subtitle {
    margin-top: 8px;
    @include Aspekta-font(400, 14px, normal, #64748b);
  }

  .video-player {
    width: 100%;
    max-height: 70vh;
    min-height: 400px;

    @media screen and (min-width: 768px) {
      min-height: 600px;
    }
  }
}
</style>
